import React, { Component } from 'react';
import getAllPostTagsWithCount from '../../../api/graphql/queries/getAllPostTagsWithCount';
import tagBulkModify from '../../../api/graphql/mutations/tagBulkModify';
import LoadIcon from '../../LoadIcon';

class TagManager extends Component {
  state = {
    tags: [],
    searchText: '',
    isLoading: true,
    editingTag: null,
    newTagName: '',
    processingTag: null,
    error: null,
    success: null,
  };

  componentDidMount() {
    this.loadTags();
  }

  loadTags = (preserveMessages) => {
    this.setState(preserveMessages ? { isLoading: true } : { isLoading: true, error: null, success: null });
    getAllPostTagsWithCount()
      .then((res) => {
        if (res && res.tags) {
          const sorted = res.tags.slice().sort((a, b) => a.tag.localeCompare(b.tag, undefined, { sensitivity: 'base' }));
          this.setState({ tags: sorted, isLoading: false });
        } else {
          this.setState({ tags: [], isLoading: false });
        }
      })
      .catch(() => {
        this.setState({ isLoading: false, error: 'Failed to load tags' });
      });
  };

  handleDelete = (tag) => {
    const isConfirmed = window.confirm(`Are you sure you want to delete the tag "${tag}" from all posts?`);
    if (!isConfirmed) return;

    this.setState({ processingTag: tag, error: null, success: null });
    tagBulkModify({ tag, action: 'delete' })
      .then((result) => {
        if (result && result.isEdited) {
          this.setState({ success: `Tag "${tag}" deleted successfully`, processingTag: null });
          this.loadTags(true);
        } else {
          this.setState({ error: result?.error || 'Failed to delete tag', processingTag: null });
        }
      })
      .catch(() => {
        this.setState({ error: 'Failed to delete tag', processingTag: null });
      });
  };

  handleRenameStart = (tag) => {
    this.setState({ editingTag: tag, newTagName: tag });
  };

  handleRenameCancel = () => {
    this.setState({ editingTag: null, newTagName: '' });
  };

  handleRenameSubmit = (tag) => {
    const { newTagName } = this.state;
    const trimmed = newTagName.trim();

    if (!trimmed || trimmed === tag) {
      this.handleRenameCancel();
      return;
    }

    this.setState({ processingTag: tag, error: null, success: null });
    tagBulkModify({ tag, action: 'rename', newTag: trimmed })
      .then((result) => {
        if (result && result.isEdited) {
          this.setState({
            success: `Tag "${tag}" renamed to "${trimmed}"`,
            processingTag: null,
            editingTag: null,
            newTagName: '',
          });
          this.loadTags(true);
        } else {
          this.setState({ error: result?.error || 'Failed to rename tag', processingTag: null });
        }
      })
      .catch(() => {
        this.setState({ error: 'Failed to rename tag', processingTag: null });
      });
  };

  render() {
    const {
      tags, searchText, isLoading, editingTag, newTagName, processingTag, error, success,
    } = this.state;

    const filteredTags = searchText
      ? tags.filter(t => t.tag.toLowerCase().includes(searchText.toLowerCase()))
      : tags;

    return (
      <div className="p-4 min-w-[500px]">
        <h2 className="font-americaMonoBold text-sm uppercase mb-2 text-white">
          Tag Manager
        </h2>
        <p className="text-grey-light text-xs mb-4">
          Rename or delete tags across all posts at once. Changes apply to post tags, meta tags, and Algolia.
        </p>

        {error && (
          <div className="bg-red-dark text-white p-2 rounded mb-3 text-xs">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-dark text-white p-2 rounded mb-3 text-xs">
            {success}
          </div>
        )}

        {isLoading ? (
          <LoadIcon />
        ) : (
          <>
            <input
              type="text"
              value={searchText}
              onChange={e => this.setState({ searchText: e.target.value })}
              placeholder="Search tags..."
              className="w-full bg-grey-darkest text-white text-xs p-2 px-3 rounded mb-3 outline-none"
            />

            <div className="mb-3 text-xs text-grey-light">
              {filteredTags.length} of {tags.length} tag{tags.length !== 1 ? 's' : ''}
            </div>

            {filteredTags.length === 0 ? (
              <p className="text-grey text-xs">No tags found.</p>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto scrollbar__black">
                {filteredTags.map(tagItem => (
                  <div
                    key={tagItem.tag}
                    className="flex items-center justify-between border-b border-grey-darkest py-2"
                  >
                    {editingTag === tagItem.tag ? (
                      <div className="flex items-center flex-1 mr-2">
                        <input
                          type="text"
                          value={newTagName}
                          onChange={e => this.setState({ newTagName: e.target.value })}
                          onKeyDown={(e) => {
                            if (processingTag === tagItem.tag) return;
                            if (e.key === 'Enter') this.handleRenameSubmit(tagItem.tag);
                            if (e.key === 'Escape') this.handleRenameCancel();
                          }}
                          className="bg-grey-darkest text-white text-xs p-1 px-2 rounded flex-1 outline-none"
                          autoFocus
                          disabled={processingTag === tagItem.tag}
                        />
                        <button
                          type="button"
                          onClick={() => this.handleRenameSubmit(tagItem.tag)}
                          className="ml-2 text-green text-xs font-americaMonoBold"
                          disabled={processingTag === tagItem.tag}
                        >
                          {processingTag === tagItem.tag ? 'Processing...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={this.handleRenameCancel}
                          className="ml-2 text-grey text-xs font-americaMonoBold"
                          disabled={processingTag === tagItem.tag}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-white text-xs flex-1">
                          {tagItem.tag}
                          <span className="text-grey-light ml-2">({tagItem.count})</span>
                        </span>
                        <div className="flex items-center">
                          {processingTag === tagItem.tag ? (
                            <span className="text-grey text-xs">Processing...</span>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => this.handleRenameStart(tagItem.tag)}
                                className="cursor-pointer mr-3"
                                title="Rename tag"
                              >
                                <span className="font-icon icon-edit text-sm text-white" />
                              </button>
                              <button
                                type="button"
                                onClick={() => this.handleDelete(tagItem.tag)}
                                className="cursor-pointer"
                                title="Delete tag"
                              >
                                <span className="font-icon icon-trash text-sm text-red" />
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

export default TagManager;
